import React, { useState, useEffect } from 'react';
import { Player, PlayerStats } from '../../types';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (stats: PlayerStats[]) => void;
  players: Player[];
  isLoading?: boolean;
  existingStats?: PlayerStats[]; // Para editar reportes existentes
  guestPlayersOnly?: boolean; // Solo mostrar invitados y solo goles
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  players,
  isLoading = false,
  existingStats = [],
  guestPlayersOnly = false
}) => {
  const [stats, setStats] = useState<{ [playerId: string]: { goals: number; assists: number; saves: number } }>({});

  // Cargar datos existentes cuando se abre el modal
  useEffect(() => {
    if (isOpen && existingStats.length > 0) {
      const existingStatsMap: { [playerId: string]: { goals: number; assists: number; saves: number } } = {};
      existingStats.forEach(stat => {
        existingStatsMap[stat.playerId] = {
          goals: stat.goals,
          assists: stat.assists,
          saves: stat.saves
        };
      });
      setStats(existingStatsMap);
    } else if (isOpen) {
      // Limpiar stats cuando se abre para crear nuevo reporte
      setStats({});
    }
  }, [isOpen, existingStats]);

  const handleStatChange = (playerId: string, statType: 'goals' | 'assists' | 'saves', value: number) => {
    setStats(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [statType]: Math.max(0, value)
      }
    }));
  };

  const handleSubmit = () => {
    // Incluir TODOS los jugadores (usuarios e invitados)
    const statsArray: PlayerStats[] = players.map(player => ({
      playerId: player.id,
      name: player.name,
      goals: stats[player.id]?.goals || 0,
      assists: stats[player.id]?.assists || 0, // Permitir asistencias para todos
      saves: stats[player.id]?.saves || 0, // Permitir atajadas para todos
      votes: 0,
      votePercentage: 0
    }));
    
    onSubmit(statsArray);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        border: '1px solid #33383B'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ color: '#FFFFFF', margin: '0 0 8px 0', fontSize: '20px' }}>
            {existingStats.length > 0 ? '✏️ Editar Reporte Post-Partido' : '📊 Reporte Post-Partido'}
          </h2>
          <p style={{ color: '#B0BEC5', margin: 0, fontSize: '14px' }}>
            {existingStats.length > 0 
              ? 'Modifica las estadísticas de cada jugador (usuarios e invitados)'
              : 'Completa las estadísticas de cada jugador (usuarios e invitados)'
            }
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          {players.map(player => (
            <div key={player.id} style={{
              backgroundColor: '#2C2F33',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '12px',
              border: '1px solid #40444B'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#FFFFFF', fontSize: '16px' }}>
                    {player.name}
                    {!player.userId && (
                      <span style={{ 
                        backgroundColor: '#FF9800', 
                        color: '#000', 
                        padding: '2px 6px', 
                        borderRadius: '4px', 
                        fontSize: '10px', 
                        marginLeft: '8px',
                        fontWeight: 'normal'
                      }}>
                        INVITADO
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#B0BEC5', fontSize: '12px' }}>
                    {player.position} • {'★'.repeat(player.rating)}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div>
                  <label htmlFor={`report-goals-${player.id}`} style={{
                    display: 'block',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    marginBottom: '4px',
                    fontWeight: '600'
                  }}>
                    ⚽ Goles
                  </label>
                  <input
                    id={`report-goals-${player.id}`}
                    type="number"
                    min="0"
                    value={stats[player.id]?.goals || 0}
                    onChange={(e) => handleStatChange(player.id, 'goals', parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #40444B',
                      backgroundColor: '#181A1B',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor={`report-assists-${player.id}`} style={{
                    display: 'block',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    marginBottom: '4px',
                    fontWeight: '600'
                  }}>
                    🎯 Asistencias
                  </label>
                  <input
                    id={`report-assists-${player.id}`}
                    type="number"
                    min="0"
                    value={stats[player.id]?.assists || 0}
                    onChange={(e) => handleStatChange(player.id, 'assists', parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #40444B',
                      backgroundColor: '#181A1B',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor={`report-saves-${player.id}`} style={{
                    display: 'block',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    marginBottom: '4px',
                    fontWeight: '600'
                  }}>
                    🧤 Atajadas
                  </label>
                  <input
                    id={`report-saves-${player.id}`}
                    type="number"
                    min="0"
                    value={stats[player.id]?.saves || 0}
                    onChange={(e) => handleStatChange(player.id, 'saves', parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #40444B',
                      backgroundColor: '#181A1B',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#40444B',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {isLoading ? 'Guardando...' : (existingStats.length > 0 ? 'Actualizar Reporte' : 'Guardar Reporte')}
          </button>
        </div>
      </div>
    </div>
  );
}; 